import React, { useEffect, useRef, useCallback } from 'react';
import { useMap } from 'react-map-gl';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SlamPlacement {
    lat: number;
    lng: number;
    bearingDegrees: number;
}

export interface SlamOverlayData {
    imageUrl: string;
    imageWidth: number;
    imageHeight: number;
    metersPerPixel: number;
    geoAnchor?: {
        lat: number;
        lng: number;
        anchorPixelX: number;
        anchorPixelY: number;
        bearingDegrees: number;
    } | null;
}

interface SlamMapOverlayProps {
    /** Unique key for this overlay (e.g. slam map id) */
    overlayId: string | number;
    /** Full SLAM map data */
    slamData: SlamOverlayData;
    /** Manual placement for unanchored maps (from user drag/rotate) */
    placement?: SlamPlacement | null;
    /** Called when user drags the overlay to a new position (only for editable unanchored maps) */
    onPlacementChange?: (placement: SlamPlacement) => void;
    /** Allows drag/cursor handling for unanchored maps */
    editable?: boolean;
    /** Opacity of the raster overlay (0–1) */
    opacity?: number;
}

// ─── Coordinate math ──────────────────────────────────────────────────────────

const DEG_TO_RAD = Math.PI / 180;
const EARTH_RADIUS = 6371000;

/**
 * Convert a local meter offset (relative to center) to GPS,
 * applying a rotation (bearing) so the image "up" aligns with the bearing direction.
 */
function localMetersToGps(
    mx: number, my: number,
    centerLat: number, centerLng: number,
    bearingDeg: number
): [number, number] {
    const bearing = bearingDeg * DEG_TO_RAD;
    const northM = my * Math.cos(bearing) - mx * Math.sin(bearing);
    const eastM = my * Math.sin(bearing) + mx * Math.cos(bearing);

    const dlat = (northM / EARTH_RADIUS) * (180 / Math.PI);
    const dlng = (eastM / (EARTH_RADIUS * Math.cos(centerLat * DEG_TO_RAD))) * (180 / Math.PI);

    return [centerLng + dlng, centerLat + dlat]; // [lng, lat]
}

/**
 * Compute the effective center lat/lng for an anchored SLAM map.
 */
function computeEffectiveCenter(data: SlamOverlayData): { lat: number; lng: number; bearing: number } {
    if (data.geoAnchor) {
        const { lat, lng, anchorPixelX, anchorPixelY, bearingDegrees } = data.geoAnchor;
        const mpp = data.metersPerPixel;
        const imgW = data.imageWidth;
        const imgH = data.imageHeight;

        const dxPx = (imgW / 2) - anchorPixelX;
        const dyPx = anchorPixelY - (imgH / 2);
        const dxM = dxPx * mpp;
        const dyM = dyPx * mpp;

        const [cLng, cLat] = localMetersToGps(dxM, dyM, lat, lng, bearingDegrees);
        return { lat: cLat, lng: cLng, bearing: bearingDegrees };
    }
    return { lat: 0, lng: 0, bearing: 0 };
}

/**
 * Compute the 4 corner GPS coordinates for a SLAM map image overlay.
 * Returns corners in the order Mapbox expects: [top-left, top-right, bottom-right, bottom-left]
 */
function computeCorners(
    centerLat: number, centerLng: number,
    bearingDeg: number,
    imageWidth: number, imageHeight: number,
    metersPerPixel: number
): [[number, number], [number, number], [number, number], [number, number]] {
    const halfW = (imageWidth * metersPerPixel) / 2;
    const halfH = (imageHeight * metersPerPixel) / 2;

    const topLeft = localMetersToGps(-halfW, halfH, centerLat, centerLng, bearingDeg);
    const topRight = localMetersToGps(halfW, halfH, centerLat, centerLng, bearingDeg);
    const bottomRight = localMetersToGps(halfW, -halfH, centerLat, centerLng, bearingDeg);
    const bottomLeft = localMetersToGps(-halfW, -halfH, centerLat, centerLng, bearingDeg);

    return [topLeft, topRight, bottomRight, bottomLeft];
}

// ─── Component ────────────────────────────────────────────────────────────────

const SlamMapOverlay: React.FC<SlamMapOverlayProps> = ({
    overlayId,
    slamData,
    placement,
    onPlacementChange,
    editable = true,
    opacity = 0.85,
}) => {
    const { current: mapRef } = useMap();
    const isDraggingRef = useRef(false);
    const dragStartRef = useRef<{ lat: number; lng: number; placementLat: number; placementLng: number } | null>(null);
    const lastDragPlacementRef = useRef<SlamPlacement | null>(null);
    const sourceAddedRef = useRef(false);

    const sourceId = `slam-overlay-${overlayId}`;
    const layerId = `slam-overlay-layer-${overlayId}`;

    // Determine center and bearing
    const isAnchored = !!(slamData.geoAnchor);
    const effectiveCenter = isAnchored ? computeEffectiveCenter(slamData) : null;

    const centerLat = isAnchored ? effectiveCenter!.lat : (placement?.lat ?? 0);
    const centerLng = isAnchored ? effectiveCenter!.lng : (placement?.lng ?? 0);
    const bearing = isAnchored ? effectiveCenter!.bearing : (placement?.bearingDegrees ?? 0);

    const hasValidPosition = centerLat !== 0 || centerLng !== 0;

    // ── Add image source+layer once ───────────────────────────────────────────

    useEffect(() => {
        if (!mapRef || !hasValidPosition || !slamData.imageUrl) return;

        const map = mapRef.getMap();
        if (!map) return;

        const corners = computeCorners(
            centerLat, centerLng, bearing,
            slamData.imageWidth, slamData.imageHeight, slamData.metersPerPixel
        );

        const applyOverlay = () => {
            // Remove existing if present
            try { if (map.getLayer(layerId)) map.removeLayer(layerId); } catch { /* */ }
            try { if (map.getSource(sourceId)) map.removeSource(sourceId); } catch { /* */ }

            map.addSource(sourceId, {
                type: 'image',
                url: slamData.imageUrl,
                coordinates: corners,
            });

            map.addLayer({
                id: layerId,
                type: 'raster',
                source: sourceId,
                paint: {
                    'raster-opacity': opacity,
                },
            });

            sourceAddedRef.current = true;
        };

        // Robust approach: if style is loaded, apply immediately.
        // Otherwise, poll with requestAnimationFrame until the style is ready.
        // This avoids the race condition where 'style.load' already fired before
        // we registered the listener (common when SlamMapOverlay mounts after Map remount).
        let cancelled = false;
        if (map.isStyleLoaded()) {
            applyOverlay();
        } else {
            const waitForStyle = () => {
                if (cancelled) return;
                if (map.isStyleLoaded()) {
                    applyOverlay();
                } else {
                    requestAnimationFrame(waitForStyle);
                }
            };
            requestAnimationFrame(waitForStyle);
        }

        return () => {
            cancelled = true;
            try {
                if (map.isStyleLoaded?.()) {
                    if (map.getLayer(layerId)) map.removeLayer(layerId);
                    if (map.getSource(sourceId)) map.removeSource(sourceId);
                }
            } catch { /* */ }
            sourceAddedRef.current = false;
        };
        // Re-create when image URL, overlay identity, or position validity changes.
        // Position updates (lat/lng/bearing) are handled by the coordinate-update effect below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapRef, slamData.imageUrl, overlayId, opacity, hasValidPosition, centerLat, centerLng, bearing]);

    // ── Update coordinates in-place (fast — no source/layer rebuild) ──────────

    useEffect(() => {
        if (!mapRef || !hasValidPosition || !sourceAddedRef.current) return;
        const map = mapRef.getMap();
        if (!map) return;

        const source = map.getSource(sourceId) as any;
        if (!source || typeof source.setCoordinates !== 'function') return;

        const corners = computeCorners(
            centerLat, centerLng, bearing,
            slamData.imageWidth, slamData.imageHeight, slamData.metersPerPixel
        );

        source.setCoordinates(corners);
        map.triggerRepaint();
    }, [mapRef, hasValidPosition, centerLat, centerLng, bearing,
        slamData.imageWidth, slamData.imageHeight, slamData.metersPerPixel, sourceId]);

    // ── Drag handling for unanchored maps ──────────────────────────────────────

    // Use refs for the latest values so drag handlers don't need to be re-registered
    const centerLatRef = useRef(centerLat);
    const centerLngRef = useRef(centerLng);
    const bearingRef = useRef(bearing);
    const onPlacementChangeRef = useRef(onPlacementChange);
    centerLatRef.current = centerLat;
    centerLngRef.current = centerLng;
    bearingRef.current = bearing;
    onPlacementChangeRef.current = onPlacementChange;

    const isInsideOverlay = useCallback((lngLat: { lat: number; lng: number }): boolean => {
        const corners = computeCorners(
            centerLatRef.current, centerLngRef.current, bearingRef.current,
            slamData.imageWidth, slamData.imageHeight, slamData.metersPerPixel
        );
        const lngs = corners.map(c => c[0]);
        const lats = corners.map(c => c[1]);
        return lngLat.lng >= Math.min(...lngs) && lngLat.lng <= Math.max(...lngs)
            && lngLat.lat >= Math.min(...lats) && lngLat.lat <= Math.max(...lats);
    }, [slamData.imageWidth, slamData.imageHeight, slamData.metersPerPixel]);

    useEffect(() => {
        if (!mapRef || isAnchored || !editable || !onPlacementChange || !hasValidPosition) return;
        const map = mapRef.getMap();
        if (!map) return;
        const canvas = map.getCanvas();

        const onMouseDown = (e: any) => {
            if (!isInsideOverlay(e.lngLat)) return;
            isDraggingRef.current = true;
            dragStartRef.current = {
                lat: e.lngLat.lat, lng: e.lngLat.lng,
                placementLat: centerLatRef.current, placementLng: centerLngRef.current,
            };
            map.dragPan.disable();
            canvas.style.cursor = 'grabbing';
            e.preventDefault();
        };

        const onMouseMove = (e: any) => {
            if (!isDraggingRef.current || !dragStartRef.current) {
                if (isInsideOverlay(e.lngLat)) canvas.style.cursor = 'grab';
                else canvas.style.cursor = '';
                return;
            }
            const dlat = e.lngLat.lat - dragStartRef.current.lat;
            const dlng = e.lngLat.lng - dragStartRef.current.lng;
            const newPlacement: SlamPlacement = {
                lat: dragStartRef.current.placementLat + dlat,
                lng: dragStartRef.current.placementLng + dlng,
                bearingDegrees: bearingRef.current,
            };

            // Immediately update the Mapbox source coordinates for smooth visual feedback
            // Do NOT call onPlacementChange here — React re-renders during drag cause flicker.
            // We store the latest placement and notify React only on mouseup.
            const source = map.getSource(sourceId) as any;
            if (source && typeof source.setCoordinates === 'function') {
                const corners = computeCorners(
                    newPlacement.lat, newPlacement.lng, newPlacement.bearingDegrees,
                    slamData.imageWidth, slamData.imageHeight, slamData.metersPerPixel
                );
                source.setCoordinates(corners);
            }

            // Track the position in a ref (for isInsideOverlay + mouseup commit)
            lastDragPlacementRef.current = newPlacement;
            centerLatRef.current = newPlacement.lat;
            centerLngRef.current = newPlacement.lng;
        };

        const onMouseUp = () => {
            if (isDraggingRef.current) {
                isDraggingRef.current = false;
                dragStartRef.current = null;
                map.dragPan.enable();
                canvas.style.cursor = '';

                // Commit the final position to React state (single update, no flicker)
                if (lastDragPlacementRef.current) {
                    onPlacementChangeRef.current?.(lastDragPlacementRef.current);
                    lastDragPlacementRef.current = null;
                }
            }
        };

        map.on('mousedown', onMouseDown);
        map.on('mousemove', onMouseMove);
        map.on('mouseup', onMouseUp);
        window.addEventListener('mouseup', onMouseUp);

        return () => {
            map.off('mousedown', onMouseDown);
            map.off('mousemove', onMouseMove);
            map.off('mouseup', onMouseUp);
            window.removeEventListener('mouseup', onMouseUp);
            map.dragPan.enable();
            if (canvas) canvas.style.cursor = '';
        };
    }, [mapRef, isAnchored, hasValidPosition, isInsideOverlay, sourceId,
        slamData.imageWidth, slamData.imageHeight, slamData.metersPerPixel, editable, onPlacementChange]);

    return null;
};

export default SlamMapOverlay;
export { computeCorners, localMetersToGps, computeEffectiveCenter };
